/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/GatewayStatusCard.tsx
 🗓️ Created: 2026-02-16
 🧩 Description:
    Real-time gateway status card for edit mode.
    Shows registration state, active calls, and uptime.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, PhoneIncoming, PhoneOutgoing, Clock, RefreshCw, Square, Play, Zap, AlertTriangle } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { fetchGatewayStatus, testGatewayConnection, reloadGateway, stopGateway } from '../api/gatewayApi';
import { GatewayStatus } from '../types';
import { useNotifications } from '../../../contexts/NotificationContext';

interface GatewayStatusCardProps {
    gatewayId: string | number;
    gatewayName: string;
}

const STATE_CONFIG: Record<string, { color: string; bgColor: string; icon: typeof Activity; label: string }> = {
    REGED: {
        color: 'text-emerald-700 dark:text-emerald-300',
        bgColor: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800',
        icon: Activity,
        label: 'gateways.status.registered'
    },
    NOREG: {
        color: 'text-slate-600 dark:text-slate-300',
        bgColor: 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700',
        icon: Activity,
        label: 'gateways.status.noRegistration'
    },
    UNREGED: {
        color: 'text-orange-700 dark:text-orange-300',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
        icon: AlertTriangle,
        label: 'gateways.status.unregistered'
    },
    FAIL_WAIT: {
        color: 'text-red-700 dark:text-red-300',
        bgColor: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800',
        icon: AlertTriangle,
        label: 'gateways.status.failed'
    },
    TRYING: {
        color: 'text-amber-700 dark:text-amber-300',
        bgColor: 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
        icon: Zap,
        label: 'gateways.status.trying'
    },
    UNKNOWN: {
        color: 'text-slate-500 dark:text-slate-400',
        bgColor: 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700',
        icon: AlertTriangle,
        label: 'gateways.status.unknown'
    }
};

export default function GatewayStatusCard({ gatewayId, gatewayName }: GatewayStatusCardProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [status, setStatus] = useState<GatewayStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadStatus = useCallback(async () => {
        try {
            const data = await fetchGatewayStatus(gatewayId);
            setStatus(data);
        } catch {
            setStatus({
                state: 'UNKNOWN',
                callsIn: 0,
                callsOut: 0,
                failedCalls: 0,
                pingStatus: 'N/A',
                uptime: 'N/A'
            });
        } finally {
            setLoading(false);
        }
    }, [gatewayId]);

    useEffect(() => {
        loadStatus();
        intervalRef.current = setInterval(loadStatus, 30000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [loadStatus]);

    const handleReload = useCallback(async () => {
        setActionLoading('reload');
        try {
            const result = await reloadGateway(gatewayId);
            showSuccess(result.message || t('gateways.reloadSuccess'));
            setTimeout(loadStatus, 2000);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('gateways.reloadError'));
        } finally {
            setActionLoading(null);
        }
    }, [gatewayId, loadStatus, showError, showSuccess, t]);

    const handleStop = useCallback(async () => {
        setActionLoading('stop');
        try {
            const result = await stopGateway(gatewayId);
            showSuccess(result.message || t('gateways.stopSuccess'));
            setTimeout(loadStatus, 2000);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('gateways.stopError'));
        } finally {
            setActionLoading(null);
        }
    }, [gatewayId, loadStatus, showError, showSuccess, t]);

    const handleStart = useCallback(async () => {
        setActionLoading('start');
        try {
            const result = await reloadGateway(gatewayId);
            showSuccess(result.message || t('gateways.startSuccess'));
            setTimeout(loadStatus, 2000);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('gateways.startError'));
        } finally {
            setActionLoading(null);
        }
    }, [gatewayId, loadStatus, showError, showSuccess, t]);

    const handleTest = useCallback(async () => {
        setActionLoading('test');
        try {
            const result = await testGatewayConnection(gatewayId);
            if (result.status === 'success') {
                showSuccess(result.message);
            } else {
                showError(result.message);
            }
            if (result.details) {
                setStatus(result.details);
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : t('gateways.status.testError'));
        } finally {
            setActionLoading(null);
        }
    }, [gatewayId, showError, showSuccess, t]);

    if (loading) {
        return (
            <div className="rounded-lg border border-border/50 bg-card/50 p-4 mb-6 animate-pulse">
                <div className="h-6 w-48 bg-muted rounded mb-3" />
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-16 bg-muted rounded" />
                    ))}
                </div>
            </div>
        );
    }

    const stateKey = status?.state || 'UNKNOWN';
    const config = STATE_CONFIG[stateKey] || STATE_CONFIG.UNKNOWN;
    const StateIcon = config.icon;
    const isGatewayStopped = !status || status.state === 'UNKNOWN';

    return (
        <div className="rounded-lg border border-border/50 bg-card/50 p-4 mb-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">{t('gateways.status.title')}</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleTest}
                        disabled={!!actionLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 disabled:opacity-50"
                    >
                        <Zap className={`h-3.5 w-3.5 ${actionLoading === 'test' ? 'animate-spin' : ''}`} />
                        {t('gateways.status.test')}
                    </button>
                    <button
                        onClick={handleReload}
                        disabled={!!actionLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${actionLoading === 'reload' ? 'animate-spin' : ''}`} />
                        {t('gateways.reload')}
                    </button>
                    {isGatewayStopped ? (
                        <button
                            onClick={handleStart}
                            disabled={!!actionLoading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 disabled:opacity-50"
                        >
                            <Play className={`h-3.5 w-3.5 ${actionLoading === 'start' ? 'animate-pulse' : ''}`} />
                            {t('gateways.start') || 'Start Trunk/Gateway'}
                        </button>
                    ) : (
                        <button
                            onClick={handleStop}
                            disabled={!!actionLoading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50 disabled:opacity-50"
                        >
                            <Square className={`h-3.5 w-3.5 ${actionLoading === 'stop' ? 'animate-pulse' : ''}`} />
                            {t('gateways.stop') || 'Stop Trunk/Gateway'}
                        </button>
                    )}
                </div>
            </div>

            {/* Status Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Registration State */}
                <div className={`rounded-lg border p-3 ${config.bgColor}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <StateIcon className={`h-4 w-4 ${config.color}`} />
                        <span className="text-xs font-medium text-muted-foreground">{t('gateways.status.state')}</span>
                    </div>
                    <p className={`text-sm font-bold ${config.color}`}>{t(config.label)}</p>
                </div>

                {/* Inbound Calls */}
                <div className="rounded-lg border border-border/50 bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <PhoneIncoming className="h-4 w-4 text-green-500" />
                        <span className="text-xs font-medium text-muted-foreground">{t('gateways.status.callsIn')}</span>
                    </div>
                    <p className="text-lg font-bold text-foreground">{status?.callsIn ?? 0}</p>
                </div>

                {/* Outbound Calls */}
                <div className="rounded-lg border border-border/50 bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <PhoneOutgoing className="h-4 w-4 text-blue-500" />
                        <span className="text-xs font-medium text-muted-foreground">{t('gateways.status.callsOut')}</span>
                    </div>
                    <p className="text-lg font-bold text-foreground">{status?.callsOut ?? 0}</p>
                </div>

                {/* Failed Calls */}
                <div className="rounded-lg border border-border/50 bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-xs font-medium text-muted-foreground">{t('gateways.status.failedCalls')}</span>
                    </div>
                    <p className="text-lg font-bold text-foreground">{status?.failedCalls ?? 0}</p>
                </div>

                {/* Uptime */}
                <div className="rounded-lg border border-border/50 bg-background/50 p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-purple-500" />
                        <span className="text-xs font-medium text-muted-foreground">{t('gateways.status.uptime')}</span>
                    </div>
                    <p className="text-sm font-bold text-foreground truncate">{status?.uptime ?? 'N/A'}</p>
                </div>
            </div>
        </div>
    );
}
